import { useEffect, useMemo } from 'react';
import { useForm, yupResolver } from '@mantine/form';
import PropTypes from 'prop-types';
import { LinearProgress } from '@mui/material';
import { format } from 'date-fns';
import { IconPlus, IconTrash } from '@tabler/icons-react';

import { AlertMessage, Button, DatePicker, IconButton, Loader, Textarea } from 'ui';

import { useAccount } from '../../store/account/hooks';
import { StepNavBottom } from '../onboarding';
import ProfileFeedback from './ProfileFeedback';
import ProfileQuestionStatus from './ProfileQuestionStatus';
import UploadSupportingDocumentInput from './UploadSupportingDocumentsInput';
import { useProfileFeedback } from './useProfileFeedback';
import {
  buildProfileAnswers,
  EMAIL_SIGNERS_INPUT,
  getDefaultEmailSigners,
  getProfileCompletionSummary,
  getProfileInputKey,
  getProfileQuestionDomId,
  getProfileQuestionRailClassName,
  isProfileFeedbackEligible,
  isProfileScoreGateSatisfied,
  PROFILE_QUESTION_STATUS,
  profileFormValidation,
  profileInputs,
} from './utils';

const getDefaultListLength = (input) => input.defaultItems ?? input.minItems ?? 1;

const buildListInitialValue = (input, value) => {
  const normalized = Array.isArray(value) ? value : value || value === 0 ? [value] : [];

  const sanitizedValues = normalized.map((item) => (typeof item === 'string' ? item : `${item ?? ''}`));
  const targetLength = Math.max(getDefaultListLength(input), sanitizedValues.length || 0);

  if (sanitizedValues.length >= targetLength) return sanitizedValues;

  return [...sanitizedValues, ...Array(targetLength - sanitizedValues.length).fill('')];
};

const getInitialValues = (profile = {}) => {
  const defaultEmailSigners = getDefaultEmailSigners(profile);

  return profileInputs.reduce((acc, input) => {
    const key = getProfileInputKey(input);
    const value = input === EMAIL_SIGNERS_INPUT && defaultEmailSigners ? defaultEmailSigners : profile?.[key];

    if (input.type === 'list') {
      acc[key] = buildListInitialValue(input, value);
    } else {
      acc[key] = value ?? '';
    }

    return acc;
  }, {});
};

const sanitizeProfileValues = (values) => {
  return profileInputs.reduce((acc, input) => {
    const key = getProfileInputKey(input);
    const value = values[key];

    if (input.type === 'list') {
      acc[key] = Array.isArray(value) ? value.map((item) => item?.trim()).filter(Boolean) : [];
    } else {
      acc[key] = value ?? '';
    }

    return acc;
  }, {});
};

const OrganizationProfileForm = ({ onboarding, onNext, onPrev }) => {
  const { loading, account, saveProfile, saveProfileFeedback, success, error, setSuccess, setError } = useAccount();
  const initialProfileValues = useMemo(() => getInitialValues(account?.profile), [account?.profile]);
  const form = useForm({
    initialValues: initialProfileValues,
    validate: yupResolver(profileFormValidation),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    form.setValues(initialProfileValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProfileValues]);

  const handleCancel = () => {
    form.reset();
  };

  const isFormValid = form.isValid();

  const isFormDirty = form.isDirty();

  const feedbackEnabled = !account?.sandbox;

  const { feedbackByKey, loadingByKey, requestFeedback, clearFeedback, getFeedback } = useProfileFeedback({
    enabled: feedbackEnabled,
    initialFeedback: account?.profileFeedback,
  });

  // DonorSpring staff can waive the minimum-score requirement for an individual
  // customer from the customer-management app. The waiver only lifts the
  // onboarding blocker: in Settings the profile still reports which answers are
  // worth strengthening, since nothing is being blocked there anyway.
  const scoreRequirementWaived = !!account?.bypassProfileScoreRequirement;
  const scoreRequired = !(onboarding && scoreRequirementWaived);

  // During onboarding the user may only advance once every scored question has
  // been graded "Strong". Waived customers, and cases where feedback is disabled
  // (e.g. sandbox/demo, which has no backend), skip the gate so onboarding is
  // not permanently blocked.
  const scoreGateSatisfied = useMemo(
    () => isProfileScoreGateSatisfied(form.values, feedbackByKey, { scoreRequired, feedbackEnabled }),
    [scoreRequired, feedbackEnabled, form.values, feedbackByKey],
  );

  const canProceed = isFormValid && scoreGateSatisfied;

  // Request AI feedback for a single field when it loses focus. Generated
  // feedback is held in local state and only persisted on form save (so the
  // saved profile and its feedback stay in sync). Inputs excluded from feedback
  // (dates, opted-out questions) are skipped entirely.
  const handleFieldBlur = (input) => {
    if (!isProfileFeedbackEligible(input)) return;

    const key = getProfileInputKey(input);
    const answers = buildProfileAnswers(form.values, [key]);

    if (answers.length > 0) {
      requestFeedback(answers);
    } else {
      clearFeedback([key]);
    }
  };

  // Per-question status plus the header roll-up. Derived from the values (not
  // from form.errors) so an unanswered question is marked immediately instead of
  // only after it has been blurred.
  const {
    statuses: questionStatuses,
    unanswered,
    notStrong,
    percentage: completionPercentage,
  } = useMemo(
    () => getProfileCompletionSummary(form.values, { feedbackByKey, feedbackEnabled, scoreRequired }),
    [form.values, feedbackByKey, feedbackEnabled, scoreRequired],
  );

  const getCompletionClassName = () => {
    if (completionPercentage >= 90) return '!bg-green-500';
    if (completionPercentage >= 50) return '!bg-orange-500';
    return '!bg-red-500';
  };

  // One task-oriented line instead of two bare counters. Shown in both the
  // onboarding and settings views: the score gate blocks progress in onboarding
  // and leaves the profile unfinished in settings, so both need to name it.
  const getStatusSummary = () => {
    const goal = onboarding ? 'to continue' : 'to finish your profile';

    if (unanswered > 0 && notStrong > 0) {
      return `Answer ${unanswered} more ${unanswered === 1 ? 'question' : 'questions'} and improve ${notStrong} ${notStrong === 1 ? 'answer' : 'answers'} ${goal}.`;
    }

    if (unanswered > 0) {
      return `Answer ${unanswered} more ${unanswered === 1 ? 'question' : 'questions'} ${goal}.`;
    }

    if (notStrong > 0) {
      return `Improve ${notStrong} ${notStrong === 1 ? 'answer' : 'answers'} scored below 80 ${goal}.`;
    }

    return '';
  };

  // Scrolls the first unfinished question into view and focuses its first input.
  // Unanswered questions are addressed before answered-but-not-strong ones.
  const scrollToFirstProblem = () => {
    const unansweredIndex = questionStatuses.findIndex(
      ({ status }) => status === PROFILE_QUESTION_STATUS.missing || status === PROFILE_QUESTION_STATUS.incomplete,
    );

    const index =
      unansweredIndex === -1
        ? questionStatuses.findIndex(({ status }) => status !== PROFILE_QUESTION_STATUS.done)
        : unansweredIndex;

    if (index === -1) return;

    const element = document.getElementById(getProfileQuestionDomId(index));
    if (!element) return;

    element.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    element.querySelector('textarea, input')?.focus?.();
  };

  const handleSave = async (values) => {
    const sanitizedValues = sanitizeProfileValues(values);
    await saveProfile(sanitizedValues);

    // Persist the current feedback together with the profile so the two never
    // get out of sync in the database. getFeedback() returns the freshest map
    // (any feedback generated on blur this session, plus previously stored).
    const feedback = getFeedback();
    if (feedbackEnabled && feedback && Object.keys(feedback).length > 0) {
      await saveProfileFeedback(feedback);
    }

    if (!onboarding) return;

    if (canProceed) onNext();
  };

  // Next: save, then either advance or point at what is still blocking. Reached
  // only when the form validates, so the remaining blocker is the score gate.
  const handleNext = async (values) => {
    await handleSave(values);
    if (!canProceed) scrollToFirstProblem();
  };

  // "Save changes" intentionally accepts an invalid (partial) form, but the click
  // is still an explicit request for feedback: validate first so every untouched
  // empty question surfaces its error instead of looking finished.
  const handleSaveClick = () => {
    form.validate();
    handleSave(form.values);
  };

  return (
    <div className="relative w-full space-y-12">
      <div
        className={`sticky top-0 z-20 flex items-center justify-between gap-4 rounded-lg border border-gray-100 bg-white-100 p-8 ${onboarding ? 'w-full' : 'max-w-4xl'}`}
      >
        <div className="space-y-4">
          <h1 className="text-h1 font-semibold">Organization Profile</h1>
          <p className="text-lg">
            Improve DonorSpring&apos;s suggested communications by telling us about your organization.
          </p>
        </div>
        <div className="flex items-start gap-6">
          <div className="w-56">
            <div className="text-lg font-semibold">{Math.round(completionPercentage)}% Complete</div>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              classes={{
                root: '!bg-gray-50 rounded !h-1.5',
                bar: getCompletionClassName(),
              }}
            />
            {!!getStatusSummary() && <p className="mt-2 text-sm leading-tight text-gray-700">{getStatusSummary()}</p>}
          </div>
        </div>
      </div>
      <div className={onboarding ? 'w-full' : 'max-w-4xl rounded-xl bg-white-100 p-8'}>
        <form noValidate onSubmit={form.onSubmit(handleSave)} className="space-y-8">
          <div className="space-y-10">
            {profileInputs.map((input, index) => {
              const inputKey = getProfileInputKey(input);
              const questionStatus = questionStatuses[index];
              const domId = getProfileQuestionDomId(index);
              const isUnanswered =
                questionStatus.status === PROFILE_QUESTION_STATUS.missing ||
                questionStatus.status === PROFILE_QUESTION_STATUS.incomplete;
              // A green "91/100 · Strong" beside a red "Needs 1 more answer" reads
              // as two contradictory verdicts. The score grades what is written so
              // far; hold it back until the question is actually answered.
              const showFeedback = isProfileFeedbackEligible(input) && !isUnanswered;
              // Status rail down the left edge of the question block.
              const railClassName = `-ml-4 border-l-2 pl-3.5 ${getProfileQuestionRailClassName(questionStatus.status)}`;

              if (input.type === 'date') {
                return (
                  <div key={inputKey} id={domId} className={`${railClassName} flex items-start gap-4`}>
                    <DatePicker
                      label={input.label}
                      containerClassName="w-[360px]"
                      fromDate={new Date('1800-01-02')}
                      required
                      {...form.getInputProps(inputKey)}
                      value={form.values[inputKey] ? new Date(form.values[inputKey]) : null}
                      onChange={(date) => form.setFieldValue(inputKey, date ? format(date, 'MM/dd/yyyy') : '')}
                    />
                    <div className="pt-7">
                      <ProfileQuestionStatus status={questionStatus} />
                    </div>
                  </div>
                );
              }

              if (input.type === 'list') {
                const listValues = Array.isArray(form.values[inputKey]) ? form.values[inputKey] : [];
                const minItems = input.minItems ?? 1;

                return (
                  <div key={inputKey} id={domId} className={`${railClassName} space-y-3`}>
                    <div className="space-y-1">
                      <div className="flex items-start justify-between gap-4">
                        <p className="text-base font-medium text-gray-950">
                          {input.label}
                          <span className="z-[1] text-base text-red-500">*</span>
                          <ProfileQuestionStatus status={questionStatus} loading={loadingByKey[inputKey]} />
                        </p>
                        {showFeedback && (
                          <ProfileFeedback feedback={feedbackByKey[inputKey]} loading={loadingByKey[inputKey]} />
                        )}
                      </div>
                      {input.helperText && <p className="text-sm text-gray-600">{input.helperText}</p>}
                    </div>

                    <div className="space-y-4">
                      {listValues.map((_, itemIndex) => {
                        const itemProps = form.getInputProps(`${inputKey}.${itemIndex}`);
                        const itemError = form.errors[`${inputKey}.${itemIndex}`];

                        return (
                          <div key={`${inputKey}-${itemIndex}`} className="flex items-start space-x-3">
                            <div className="flex-1">
                              <Textarea
                                helperText={itemError || undefined}
                                error={!!itemError}
                                rows={1}
                                inputClassName="field-sizing-content min-h-10"
                                {...itemProps}
                                onBlur={(event) => {
                                  itemProps.onBlur?.(event);
                                  handleFieldBlur(input);
                                }}
                              />
                            </div>

                            <IconButton
                              Icon={<IconTrash size={16} className="text-gray-500" />}
                              srOnly="Delete item"
                              color="transparent"
                              className="mt-1 !p-2"
                              onClick={() => form.removeListItem(inputKey, itemIndex)}
                              disabled={listValues.length <= minItems}
                            />
                          </div>
                        );
                      })}
                    </div>

                    {/* Sits with the rows it applies to, not next to Add Item. */}
                    {typeof form.errors[inputKey] === 'string' && (
                      <p className="text-sm italic text-red-500">{form.errors[inputKey]}</p>
                    )}

                    <div className="flex items-center space-x-4">
                      <Button
                        title="Add Item"
                        color="primary"
                        variant="outlined"
                        size="sm"
                        type="button"
                        onClick={() => form.insertListItem(inputKey, '')}
                        LeftIcon={IconPlus}
                      />
                    </div>
                  </div>
                );
              }

              const textProps = form.getInputProps(inputKey);
              const textError = form.errors[inputKey];

              return (
                <div key={inputKey} id={domId} className={`${railClassName} space-y-1.5`}>
                  <div className="space-y-1">
                    <div className="flex items-start justify-between gap-4">
                      <label htmlFor={inputKey} className="text-base font-medium text-gray-950">
                        {input.label}
                        <span className="z-[1] text-base text-red-500">*</span>
                        <ProfileQuestionStatus status={questionStatus} loading={loadingByKey[inputKey]} />
                      </label>
                      {showFeedback && (
                        <ProfileFeedback feedback={feedbackByKey[inputKey]} loading={loadingByKey[inputKey]} />
                      )}
                    </div>
                    {input.helperText && <p className="text-sm text-gray-600">{input.helperText}</p>}
                  </div>
                  <Textarea
                    id={inputKey}
                    inputClassName="field-sizing-content min-h-32"
                    helperText={textError}
                    error={!!textError}
                    {...textProps}
                    onBlur={(event) => {
                      textProps.onBlur?.(event);
                      handleFieldBlur(input);
                    }}
                  />
                </div>
              );
            })}

            <div className="space-y-6">
              <div className="space-y-2">
                <h3 className="text-h6 font-semibold">Supporting Documents</h3>

                <p className="text-base">
                  Upload any written materials (grant applications, marketing documents, annual reports, past
                  fundraising emails, etc.) you have about the organization or its work. After upload, an optional
                  description may be added to give us additional context about the document&apos;s contents.
                </p>
              </div>
              <UploadSupportingDocumentInput />
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              title="Save changes"
              color="primary"
              type="button"
              loading={loading}
              disabled={loading || !isFormDirty}
              onClick={handleSaveClick} // intentionally allow invalid (partial) saves
            />

            {!onboarding && <Button title="Cancel" onClick={handleCancel} disabled={loading || !isFormDirty} />}
          </div>
        </form>
      </div>

      {loading && <Loader />}

      {onboarding && (
        <StepNavBottom
          onPrev={onPrev}
          prevDisabled={loading}
          // Deliberately not gated on validity: a disabled Next is the reason the
          // blocking questions were invisible. Submitting an invalid form reveals
          // every error and scrolls to the first one instead of advancing.
          onNext={form.onSubmit(handleNext, scrollToFirstProblem)}
          nextDisabled={loading}
        />
      )}

      <AlertMessage
        open={!!success}
        message={typeof success === 'string' ? success : 'Operation completed successfully!'}
        onClose={() => setSuccess(false)}
        severity="success"
      />

      <AlertMessage
        open={!!error}
        message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
        onClose={setError}
        severity="error"
      />
    </div>
  );
};

OrganizationProfileForm.propTypes = {
  onboarding: PropTypes.bool,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func,
};

OrganizationProfileForm.defaultProps = {
  onboarding: false,
  onPrev: () => {},
  onNext: () => {},
};

export default OrganizationProfileForm;
